import { motion } from 'framer-motion';
import { Shield, AlertTriangle, FileText, Users, ClipboardCheck, Scale } from 'lucide-react';

export default function LegalPage() {
  const sections = [
    {
      id: 'nature',
      icon: FileText,
      title: 'Nature du service',
      content: `Assistant by Léon propose un service numérique d'assistance à l'analyse de devis et documents travaux reposant notamment sur des traitements automatisés. Les résultats, scores, alertes, recommandations, emails générés et rapports PDF sont fournis à titre strictement indicatif pour aider l'utilisateur à apprécier certains points d'attention.`
    },
    {
      id: 'professionnel',
      icon: Users,
      title: 'Absence de substitution à un professionnel',
      content: `Le service ne remplace pas une visite sur site, un relevé, une étude technique, une mission de maîtrise d'œuvre, un audit contradictoire, un contrôle de conformité, un conseil juridique, ni l'intervention d'un artisan, technicien, bureau d'études, expert ou assureur.`
    },
    {
      id: 'responsabilite',
      icon: Scale,
      title: 'Limites de responsabilité',
      content: `Assistant by Léon est tenu d'une obligation de moyens dans la fourniture du service numérique. L'utilisateur reconnaît que les analyses peuvent être partielles, dépendre de la qualité des documents transmis, comporter des approximations et ne pas révéler l'ensemble des risques techniques, administratifs, financiers ou contractuels. L'utilisateur demeure seul responsable de ses choix, signatures, arbitrages, paiements, validations et décisions relatives au chantier.

Sauf faute prouvée dans l'exécution du service numérique lui-même, Assistant by Léon ne répond pas des malfaçons, désordres, abandons de chantier, non-conformités, surcoûts, retards, omissions, sinistres, défauts d'assurance, erreurs de devis, ni des conséquences de l'exécution des travaux par des tiers.`
    },
    {
      id: 'verifications',
      icon: ClipboardCheck,
      title: 'Vérifications complémentaires',
      content: `Avant toute signature ou exécution de travaux, l'utilisateur doit faire procéder, si nécessaire, à toute vérification complémentaire utile auprès de professionnels qualifiés et assurés.`
    },
    {
      id: 'documents',
      icon: AlertTriangle,
      title: 'Documents et données transmis par l\'utilisateur',
      content: `La qualité et la pertinence des analyses dépendent directement des documents transmis par l'utilisateur. Assistant by Léon ne saurait être tenu responsable des erreurs, omissions ou imprécisions résultant de documents incomplets, illisibles, falsifiés ou non représentatifs de la réalité du projet ou du chantier.`
    }
  ];

  return (
    <div className="min-h-screen bg-black-rich pt-24 pb-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gold-500/10 mb-6">
            <Shield className="w-8 h-8 text-gold-500" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
            Mentions et limites du service
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
            Conditions d'utilisation et avertissements relatifs au service Assistant by Léon
          </p>
        </motion.div>

        {/* Important Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-6 mb-8 border-gold-500/30"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gold-500/10 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-gold-500" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-white mb-2">
                Avertissement important
              </h2>
              <p className="text-zinc-300 leading-relaxed">
                Assistant by Léon fournit une analyse automatisée d'aide à la décision, à titre informatif et indicatif. Les résultats proposés ne constituent ni un diagnostic technique exhaustif, ni une mission de maîtrise d'œuvre, ni un contrôle de conformité sur site, ni un avis juridique. L'utilisateur reste seul responsable de ses décisions, signatures, paiements et choix d'entreprise.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + index * 0.05 }}
              className="glass-card p-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center flex-shrink-0">
                  <section.icon className="w-5 h-5 text-gold-500" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-bold text-white mb-3">
                    {section.title}
                  </h3>
                  <p className="text-zinc-400 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-zinc-500 text-sm">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
